import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { MapPin, ArrowUpRight } from 'lucide-react';

/**
 * PortraitCard — interactive replacement for the static About-page portrait.
 *
 * Layered effects, all subtle enough to be delightful without being distracting:
 *   1. Cursor-parallax tilt (spring-damped 3D rotate)
 *   2. Desaturate → colour reveal on hover
 *   3. Slow-rotating dashed rings around the frame
 *   4. Film-grain noise overlay (SVG turbulence, extremely low opacity)
 *   5. Cursor-follow spotlight glow that tracks the mouse across the image
 *   6. Live "Available" status pill with a pulsing emerald dot
 *   7. Hover-only "Hi." wave bubble that pops from the top-right
 */

export const PortraitCard = ({
    src,
    name,
    tagline,
    className = 'col-span-12 md:col-span-4',
    minHeight = 420,
    mobileMinHeight = 300,
    to,
    ctaLabel = 'Read my story',
}) => {
    const wrapperRef = useRef(null);
    const [hovered, setHovered] = useState(false);

    // Parallax tilt driven by cursor position within the card
    const rawX = useMotionValue(0);
    const rawY = useMotionValue(0);
    const rotX = useSpring(useTransform(rawY, [-0.5, 0.5], [6, -6]), {
        stiffness: 180,
        damping: 22,
    });
    const rotY = useSpring(useTransform(rawX, [-0.5, 0.5], [-6, 6]), {
        stiffness: 180,
        damping: 22,
    });
    // Spotlight position (0-100%)
    const spotX = useSpring(useTransform(rawX, [-0.5, 0.5], [0, 100]), {
        stiffness: 180,
        damping: 22,
    });
    const spotY = useSpring(useTransform(rawY, [-0.5, 0.5], [0, 100]), {
        stiffness: 180,
        damping: 22,
    });

    const handleMove = (e) => {
        const el = wrapperRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        rawX.set((e.clientX - rect.left) / rect.width - 0.5);
        rawY.set((e.clientY - rect.top) / rect.height - 0.5);
    };
    const handleLeave = () => {
        rawX.set(0);
        rawY.set(0);
        setHovered(false);
    };

    return (
        <div
            className={`${className} rounded-[22px] border border-[var(--line)] bg-[var(--surface)] p-0 overflow-hidden`}
            style={{
                perspective: 1200,
                // Responsive min-height via CSS var so mobile isn't stuck with
                // the desktop 380/420-px canvas (which left a blank slab above
                // the avatar on narrow col-span-6 tiles).
                ['--pc-mh-desktop']: `${minHeight}px`,
                ['--pc-mh-mobile']: `${mobileMinHeight}px`,
            }}
        >
            <motion.div
                ref={wrapperRef}
                onMouseEnter={() => setHovered(true)}
                onMouseMove={handleMove}
                onMouseLeave={handleLeave}
                onTouchStart={() => setHovered(true)}
                onTouchEnd={handleLeave}
                data-cursor="hover"
                data-testid="about-portrait-card"
                className="relative w-full h-full cursor-pointer min-h-[var(--pc-mh-mobile)] sm:min-h-[var(--pc-mh-desktop)]"
                style={{
                    rotateX: rotX,
                    rotateY: rotY,
                    transformStyle: 'preserve-3d',
                }}
            >
                {/* Base image — the avatar PNGs already ship with a transparent
                     background, so we drop straight onto the theme card surface.
                     `object-contain object-bottom` keeps the full character
                     visible with feet anchored to the card's lower edge.
                     Motion recipe:
                     - Entrance: fades and rises on mount so the character
                       "walks in" to the frame
                     - Idle: continuous floating bob (y-oscillation) so the
                       avatar never sits perfectly still
                     - Hover: gentle scale bump + subtle head-tilt (rotate) —
                       reads as a "hi there" nod without shouting
                     - Filter: contrast/saturate boost on hover for punch */}
                <motion.img
                    src={src}
                    srcSet={`${src} 1x, ${src.replace(/\.webp$/, '@2x.webp')} 2x`}
                    alt={name}
                    decoding="async"
                    fetchPriority="high"
                    initial={{ opacity: 0, y: 40 }}
                    animate={{
                        opacity: 1,
                        y: [0, -6, 0, -3, 0],
                        rotate: hovered ? [0, -1.5, 1.5, -1, 0] : 0,
                        scale: hovered ? 1.03 : 1,
                        filter: hovered
                            ? 'contrast(1.06) saturate(1.08)'
                            : 'contrast(1) saturate(1)',
                    }}
                    transition={{
                        opacity: { duration: 0.9, ease: [0.2, 0.8, 0.2, 1] },
                        y: { duration: 4.2, ease: 'easeInOut', repeat: Infinity, repeatType: 'loop' },
                        rotate: hovered
                            ? { duration: 0.8, ease: 'easeInOut' }
                            : { duration: 0.4 },
                        scale: { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] },
                        filter: { duration: 0.6, ease: [0.2, 0.8, 0.2, 1] },
                    }}
                    style={{ transformOrigin: 'center bottom' }}
                    className="absolute inset-0 w-full h-full object-contain object-bottom will-change-transform"
                />

                {/* Cursor-follow spotlight */}
                <motion.div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none mix-blend-plus-lighter"
                    style={{
                        background: useTransform(
                            [spotX, spotY],
                            ([x, y]) =>
                                `radial-gradient(180px circle at ${x}% ${y}%, rgba(216,254,91,0.20), transparent 60%)`
                        ),
                        opacity: hovered ? 1 : 0,
                        transition: 'opacity 400ms ease',
                    }}
                />

                {/* Ambient bottom vignette — dark fade so the identity block
                     at the bottom stays legible over the studio-lit avatar. */}
                <div className="absolute inset-x-0 bottom-0 h-[38%] sm:h-1/2 bg-gradient-to-t from-black/85 via-black/45 to-transparent pointer-events-none" />

                {/* Slow-rotating dashed ring (top-right decoration) */}
                <motion.svg
                    aria-hidden
                    viewBox="0 0 100 100"
                    className="absolute -top-4 -right-4 w-24 h-24 opacity-70 pointer-events-none"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
                >
                    <circle
                        cx="50"
                        cy="50"
                        r="46"
                        fill="none"
                        stroke="var(--accent, #D8FE5B)"
                        strokeWidth="0.8"
                        strokeDasharray="1.5 3"
                    />
                    <circle cx="50" cy="4" r="2.2" fill="var(--accent, #D8FE5B)" />
                </motion.svg>

                {/* Film-grain overlay */}
                <svg
                    aria-hidden
                    className="absolute inset-0 w-full h-full opacity-[0.09] mix-blend-overlay pointer-events-none"
                >
                    <filter id="portrait-grain">
                        <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="7" />
                        <feColorMatrix values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 1 0" />
                    </filter>
                    <rect width="100%" height="100%" filter="url(#portrait-grain)" />
                </svg>

                {/* Top-right status pill only — the "02 · The Designer" label
                     was removed so nothing crowds the avatar's head. */}
                <div className="absolute top-5 right-5 flex items-center justify-end">
                    <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-black/40 backdrop-blur border border-white/20">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" />
                        <span className="font-mono-brand text-[10px] uppercase tracking-[0.18em] text-white/90">
                            Available
                        </span>
                    </div>
                </div>

                {/* Hover-only "Hi." wave that pops from the corner */}
                <motion.div
                    aria-hidden
                    className="absolute top-16 right-6 px-3 py-1.5 rounded-full font-display text-[var(--accent-ink)] text-lg select-none pointer-events-none"
                    style={{
                        background: 'var(--accent)',
                        boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
                    }}
                    animate={{
                        opacity: hovered ? 1 : 0,
                        y: hovered ? 0 : -8,
                        rotate: hovered ? -6 : -2,
                        scale: hovered ? 1 : 0.85,
                    }}
                    transition={{ type: 'spring', stiffness: 300, damping: 18 }}
                >
                    Hi.
                </motion.div>

                {/* Bottom identity block */}
                <div className="absolute bottom-4 left-4 right-4 sm:bottom-5 sm:left-5 sm:right-5 z-20">
                    <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-black/40 backdrop-blur border border-white/20 text-white text-[11px]">
                        <MapPin size={11} />
                        {name}
                    </div>
                    <div className="font-display-italic text-lg sm:text-2xl md:text-4xl mt-2 sm:mt-3 text-white leading-tight">
                        {tagline}
                    </div>
                </div>

                {/* Click-through overlay + hover CTA — only when a target is supplied */}
                {to && (
                    <>
                        <Link
                            to={to}
                            aria-label={ctaLabel}
                            data-testid="portrait-card-link"
                            data-cursor="hover"
                            className="absolute inset-0 z-30"
                        />
                        <motion.div
                            aria-hidden
                            className="absolute top-16 left-6 z-30 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/55 backdrop-blur border border-white/25 text-white pointer-events-none"
                            animate={{
                                opacity: hovered ? 1 : 0,
                                y: hovered ? 0 : 6,
                            }}
                            transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
                        >
                            <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em]">
                                {ctaLabel}
                            </span>
                            <ArrowUpRight size={12} className="text-[var(--accent,#D8FE5B)]" />
                        </motion.div>
                    </>
                )}
            </motion.div>
        </div>
    );
};

export default PortraitCard;
